import { useCallback, useRef, useState } from "react";
import {
    accountDeletionService,
    createAccountDeletionIdempotencyKey,
} from "../services/accountDeletionService";
import { useClearSensitiveRefsOnPageHide } from "./useClearSensitiveRefsOnPageHide";

export const useAccountDeletionRecoverySession = () => {
    const [status, setStatus] = useState(null);
    const [hasSession, setHasSession] = useState(false);
    const [lastCheckedAt, setLastCheckedAt] = useState(null);
    const recoveryTokenRef = useRef(null);
    const confirmationTokenRef = useRef(null);
    const idempotencyKeyRef = useRef(null);
    useClearSensitiveRefsOnPageHide(
        recoveryTokenRef,
        confirmationTokenRef,
        idempotencyKeyRef,
    );

    const clear = useCallback(() => {
        recoveryTokenRef.current = null;
        confirmationTokenRef.current = null;
        idempotencyKeyRef.current = null;
        setHasSession(false);
        setStatus(null);
        setLastCheckedAt(null);
    }, []);

    const applyStatus = useCallback((nextStatus) => {
        setStatus(nextStatus);
        setLastCheckedAt(Date.now());
        return nextStatus;
    }, []);

    const acceptCredentials = useCallback(async (response) => {
        const recoveryToken = typeof response?.recoveryToken === "string"
            ? response.recoveryToken.trim()
            : "";
        const confirmationToken = typeof response?.confirmationToken === "string"
            ? response.confirmationToken.trim()
            : "";
        if (!recoveryToken || !confirmationToken) {
            throw new TypeError("Recovery credentials are unavailable");
        }
        recoveryTokenRef.current = recoveryToken;
        confirmationTokenRef.current = confirmationToken;
        idempotencyKeyRef.current = createAccountDeletionIdempotencyKey();
        setHasSession(true);

        const nextStatus = await accountDeletionService.getRecoveryStatus({ recoveryToken });
        return applyStatus(nextStatus);
    }, [applyStatus]);

    const refreshStatus = useCallback(async () => {
        if (!recoveryTokenRef.current) {
            throw new TypeError("Recovery session is unavailable");
        }
        const nextStatus = await accountDeletionService.getRecoveryStatus({
            recoveryToken: recoveryTokenRef.current,
        });
        return applyStatus(nextStatus);
    }, [applyStatus]);

    const confirmRecovery = useCallback(async () => {
        if (!recoveryTokenRef.current
            || !confirmationTokenRef.current
            || !idempotencyKeyRef.current) {
            throw new TypeError("Recovery confirmation is unavailable");
        }
        const nextStatus = await accountDeletionService.confirmRecovery({
            recoveryToken: recoveryTokenRef.current,
            confirmationToken: confirmationTokenRef.current,
            idempotencyKey: idempotencyKeyRef.current,
        });
        confirmationTokenRef.current = null;
        return applyStatus(nextStatus);
    }, [applyStatus]);

    return {
        status,
        hasSession,
        hasConfirmation: Boolean(confirmationTokenRef.current),
        lastCheckedAt,
        acceptCredentials,
        refreshStatus,
        confirmRecovery,
        clear,
    };
};
